import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { useBdList } from '../../../hooks/useBasicDesign.js';
import { formatLocalYmd } from '../../../lib/date-utils.js';
import { reviewStatusTone } from './bd-review.js';
import type { BdSummary } from '../../../lib/api/basic-design.js';

/** 親チケット 1 つと、その下の基本設計。 @graph-connects none */
interface BdGroup {
  rootKey: string;
  /** 親チケット自身に基本設計があればその件名。無ければ空文字 */
  rootTitle: string;
  items: BdSummary[];
}

/**
 * 基本設計を親チケットごとにまとめる。並びは API が返した順（bdKey 順）のまま。
 * @graph-connects none
 */
function groupByRoot(items: readonly BdSummary[]): BdGroup[] {
  const groups = new Map<string, BdGroup>();
  for (const item of items) {
    const group = groups.get(item.rootKey) ?? { rootKey: item.rootKey, rootTitle: '', items: [] };
    group.items.push(item);
    if (item.bdKey === item.rootKey) group.rootTitle = item.bdTitle;
    groups.set(item.rootKey, group);
  }
  return [...groups.values()];
}

/** リンクの色。 @graph-connects none */
const LINK_CLASS = 'text-[var(--color-accent-primary)] hover:underline';

/** 表の見出しのセル。 @graph-connects none */
const TH_CLASS =
  'whitespace-nowrap px-4 py-2 text-left text-xs font-medium text-[var(--color-text-muted)]';

/** 表の本文のセル。 @graph-connects none */
const TD_CLASS = 'border-t border-[var(--color-border)] px-4 py-3 align-top';

/**
 * 完了までの進み。完了なら緑の札、まだなら保存した節の数と必須の節の数。
 * @graph-connects none
 */
function ProgressCell({ item }: { item: BdSummary }) {
  const { t } = useTranslation();
  const { progress } = item;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {progress.done ? (
        <Badge size="sm" variant="success">
          {t('bd.progress.done')}
        </Badge>
      ) : null}
      <span className="tabular-nums text-[var(--color-text-secondary)]">
        {t('bd.progress.summary', {
          saved: progress.saved,
          total: progress.total,
          requiredSaved: progress.requiredSaved,
          requiredTotal: progress.requiredTotal,
        })}
      </span>
    </div>
  );
}

/**
 * レビューの状態。依頼していなければ「未依頼」、依頼後に中身が変わったら「要再依頼」を添える。
 * @graph-connects reviewStatusTone [calls] 状態の色
 */
function ReviewCell({ item }: { item: BdSummary }) {
  const { t } = useTranslation();
  const review = item.review;
  if (review === null) {
    return (
      <span className="text-xs text-[var(--color-text-muted)]">{t('bd.review.notRequested')}</span>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Badge size="sm" variant={reviewStatusTone(review.status)}>
        {t(`bd.review.status.${review.status}`)}
      </Badge>
      {review.stale ? (
        <Badge size="sm" variant="warning">
          {t('bd.review.staleBadge')}
        </Badge>
      ) : null}
      <span className="text-xs text-[var(--color-text-muted)]">
        {t('bd.list.reviewMeta', { reviewer: review.reviewerNickName, open: review.openFindings })}
      </span>
    </div>
  );
}

/**
 * 基本設計の一覧。親チケットごとにまとめて、各基本設計の進み具合・レビューの状態・最終更新日を出す。
 *
 * 最終更新日は見ている人の時刻帯の日付にする（UTC の文字列を切ると、日本時間の朝に保存した
 * ものが前日に見える）。
 *
 * まだ基本設計が無いチケットはこの一覧に出ないので、チケット番号から直接開ける欄を上に置く
 * （開いて保存すると、そのチケットの基本設計が一覧に出る）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の一覧を親チケットごとに進み具合・レビューの状態つきで出し、チケット番号から基本設計を開ける入口を置く
 * @graph-connects useBdList [calls] 基本設計の一覧の取得
 * @graph-connects groupByRoot [calls] 親チケットごとのまとめ
 * @graph-connects formatLocalYmd [calls] 最終更新日の整形
 * @graph-connects ReviewCell [calls] レビューの状態
 */
export function BdList() {
  const { t } = useTranslation();
  const list = useBdList();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState('');

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <header>
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">{t('bd.list.title')}</h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{t('bd.list.lead')}</p>
      </header>

      <Card padding="sm">
        <CardContent className="mt-0">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              const bdKey = ticket.trim().toUpperCase();
              if (bdKey === '') return;
              void navigate({ to: '/projects/bd/$bdKey', params: { bdKey } });
            }}
          >
            <div className="min-w-64 flex-1">
              <Input
                label={t('bd.list.openLabel')}
                size="sm"
                fullWidth
                placeholder="AIRCLOSET-150123"
                value={ticket}
                onChange={(e) => {
                  setTicket(e.target.value);
                }}
              />
            </div>
            <Button type="submit" size="sm">
              {t('bd.list.open')}
            </Button>
          </form>
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">{t('bd.list.openHint')}</p>
        </CardContent>
      </Card>

      {list.error !== null ? (
        <p className="text-sm text-[var(--color-text-secondary)]">{t('bd.list.failed')}</p>
      ) : list.data === undefined ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('bd.loading')}</p>
      ) : list.data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border-hover)] px-6 py-8 text-center text-sm text-[var(--color-text-secondary)]">
          {t('bd.list.empty')}
        </div>
      ) : (
        groupByRoot(list.data.items).map((group) => (
          <section
            key={group.rootKey}
            aria-label={
              group.rootTitle === '' ? group.rootKey : `${group.rootKey} ${group.rootTitle}`
            }
          >
            <Card padding="none" className="overflow-hidden">
              <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {group.rootTitle === '' ? group.rootKey : group.rootTitle}
                </h2>
                <Badge size="sm">{group.rootKey}</Badge>
                <a
                  className={`text-xs ${LINK_CLASS}`}
                  href={`https://air-closet.backlog.jp/view/${group.rootKey}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('bd.list.backlog')}
                </a>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-[var(--color-bg-elevated)]">
                    <tr>
                      {[
                        t('bd.list.colBd'),
                        t('bd.list.colProgress'),
                        t('bd.list.colReview'),
                        t('bd.list.colUpdated'),
                      ].map((h) => (
                        <th key={h} className={TH_CLASS}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item) => (
                      <tr key={item.bdKey} className="hover:bg-[var(--color-bg-hover)]">
                        <td className={TD_CLASS}>
                          <Link
                            to="/projects/bd/$bdKey"
                            params={{ bdKey: item.bdKey }}
                            className={`font-medium ${LINK_CLASS}`}
                          >
                            {item.bdTitle === '' ? item.bdKey : item.bdTitle}
                          </Link>
                          <span className="block text-xs text-[var(--color-text-muted)]">
                            {item.bdKey}
                          </span>
                        </td>
                        <td className={TD_CLASS}>
                          <ProgressCell item={item} />
                        </td>
                        <td className={TD_CLASS}>
                          <ReviewCell item={item} />
                        </td>
                        <td
                          className={`${TD_CLASS} whitespace-nowrap text-[var(--color-text-secondary)]`}
                        >
                          {formatLocalYmd(item.updatedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>
        ))
      )}
    </div>
  );
}
